import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography} from 'views/components';
import {ExpandMore} from 'views/icons';

import {CircularProgress, Menu, MenuItem} from '@mui/material';

const CompactDropdown = (props) => {
  const {
    label,
    menuLabel,
    noOptionsText = '-',
    onOptionSelect,
    options,
    getOptionKey,
    getOptionLabel,
    getOptionHref,
    isOptionSelected,
    loading = false,
    value,
    valueColor = '#000000',
    valueHref,
  } = props;

  const [anchorEl, setAnchorEl] = React.useState(null);

  const isMenuOpen = Boolean(anchorEl);

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleToggle = (event) => {
    const nextAnchorEl = event.currentTarget.closest('[data-dropdown-anchor="true"]');

    if (isMenuOpen) {
      closeMenu();
      return;
    }

    setAnchorEl(nextAnchorEl);
  };

  return (
    <>
      <Stack
        data-dropdown-anchor='true'
        sx={{
          gap: '6px',
          minWidth: 0,
          alignItems: 'flex-start',
        }}
      >
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '11px',
            lineHeight: '14px',
            color: '#A8A49E',
          }}
        >
          {label}
        </Typography>
        <Stack direction='row' sx={{gap: '4px', alignItems: 'center', minWidth: 0}}>
          {
            valueHref &&
            <Typography
              component='a'
              href={valueHref}
              onClick={(event) => event.stopPropagation()}
              rel='noopener noreferrer'
              sx={{
                'fontWeight': 700,
                'fontSize': '12px',
                'lineHeight': '16px',
                'color': valueColor,
                'wordBreak': 'break-word',
                'textDecoration': 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              target='_blank'
            >
              {value}
            </Typography>
          }
          {
            !valueHref &&
            <Box
              component='button'
              onClick={handleToggle}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 0,
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: valueColor,
                  wordBreak: 'break-word',
                }}
              >
                {value}
              </Typography>
            </Box>
          }
          <Box
            component='button'
            onClick={handleToggle}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
          >
            <ExpandMore
              sx={{
                color: '#A8A49E',
                fontSize: '16px',
                transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Box>
        </Stack>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        MenuListProps={{disablePadding: true}}
        onClose={closeMenu}
        open={isMenuOpen}
        slotProps={{
          paper: {
            sx: {
              mt: '8px',
              border: '1px solid #D9D8D5',
              boxShadow: '0px 4px 12.1px rgba(0, 0, 0, 0.05)',
              borderRadius: '4px',
            },
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Stack
          sx={{
            boxSizing: 'border-box',
            width: '230px',
            p: '14px 25px 25px 14px',
            gap: '10px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '11px',
              lineHeight: '14px',
              color: '#A8A49E',
              textTransform: 'uppercase',
            }}
          >
            {menuLabel}
          </Typography>
          {
            loading &&
            <Stack direction='row' sx={{alignItems: 'center', gap: '8px'}}>
              <CircularProgress size={16} sx={{color: '#A8A49E'}} />
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: '#000000',
                }}
              >
                Loading...
              </Typography>
            </Stack>
          }
          {
            !loading &&
            <Stack sx={{gap: '12px'}}>
              {
                options.length === 0 &&
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '18px',
                    color: '#000000',
                  }}
                >
                  {noOptionsText}
                </Typography>
              }
              {
                options.map((option) => {
                  const optionHref = getOptionHref?.(option);
                  const isSelected = isOptionSelected?.(option) ?? false;

                  return (
                    <MenuItem
                      key={getOptionKey(option)}
                      component={optionHref ? 'a' : 'li'}
                      href={optionHref}
                      onClick={() => {
                        closeMenu();

                        if (!optionHref) {
                          onOptionSelect(option);
                        }
                      }}
                      rel={optionHref ? 'noopener noreferrer' : undefined}
                      selected={isSelected}
                      sx={{
                        'm': 0,
                        'px': '6px',
                        'py': '2px',
                        'minHeight': '22px',
                        'borderRadius': '4px',
                        'backgroundColor': 'transparent',
                        '&:hover': {
                          'backgroundColor': '#F7F4EF',
                        },
                        '&.Mui-selected': {
                          'backgroundColor': '#F7F4EF',
                        },
                        '&.Mui-selected:hover': {
                          'backgroundColor': '#EEE8DF',
                        },
                      }}
                      target={optionHref ? '_blank' : undefined}
                    >
                      <Typography
                        sx={{
                          fontWeight: isSelected ? 500 : 400,
                          fontSize: '14px',
                          lineHeight: '18px',
                          color: isSelected ? '#DA7955' : '#000000',
                        }}
                      >
                        {getOptionLabel(option)}
                      </Typography>
                    </MenuItem>
                  );
                })
              }
            </Stack>
          }
        </Stack>
      </Menu>
    </>
  );
};

CompactDropdown.propTypes = {
  getOptionHref: PropTypes.func,
  getOptionKey: PropTypes.func.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  isOptionSelected: PropTypes.func,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  loading: PropTypes.bool,
  menuLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  noOptionsText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onOptionSelect: PropTypes.func,
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  valueColor: PropTypes.string,
  valueHref: PropTypes.string,
};

export default CompactDropdown;
